import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";

import { useVocabTermBySlug } from "@/api/extensions/vocabulary/useVocabTermBySlug";

interface OpenState {
  slug: string;
  anchorRect: DOMRect;
}

interface VocabHoverContextValue {
  open: (slug: string, anchorEl: HTMLElement) => void;
  scheduleClose: () => void;
  cancelClose: () => void;
}

const VocabHoverContext = createContext<VocabHoverContextValue | null>(null);

const OPEN_DELAY_MS = 180;
const CLOSE_DELAY_MS = 220;
const CARD_WIDTH = 360;
const CARD_MARGIN = 12;

export function useVocabHover(): VocabHoverContextValue {
  const ctx = useContext(VocabHoverContext);
  if (!ctx) {
    // No-op fallback so VocabLink can render anywhere without crashing
    // (e.g. unit tests). The popup just won't appear.
    return {
      open: () => undefined,
      scheduleClose: () => undefined,
      cancelClose: () => undefined,
    };
  }
  return ctx;
}

export function VocabHoverCardProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [state, setState] = useState<OpenState | null>(null);
  const closeTimerRef = useRef<number | null>(null);
  const openTimerRef = useRef<number | null>(null);
  const pendingRef = useRef<{ slug: string; el: HTMLElement } | null>(null);

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current !== null) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const clearOpenTimer = useCallback(() => {
    if (openTimerRef.current !== null) {
      window.clearTimeout(openTimerRef.current);
      openTimerRef.current = null;
    }
  }, []);

  const open = useCallback(
    (slug: string, anchorEl: HTMLElement) => {
      clearCloseTimer();
      pendingRef.current = { slug, el: anchorEl };
      clearOpenTimer();
      openTimerRef.current = window.setTimeout(() => {
        const pending = pendingRef.current;
        if (!pending) return;
        setState({
          slug: pending.slug,
          anchorRect: pending.el.getBoundingClientRect(),
        });
      }, OPEN_DELAY_MS);
    },
    [clearCloseTimer, clearOpenTimer],
  );

  const scheduleClose = useCallback(() => {
    clearOpenTimer();
    pendingRef.current = null;
    if (closeTimerRef.current !== null) return;
    closeTimerRef.current = window.setTimeout(() => {
      setState(null);
      closeTimerRef.current = null;
    }, CLOSE_DELAY_MS);
  }, [clearOpenTimer]);

  const cancelClose = useCallback(() => {
    clearCloseTimer();
  }, [clearCloseTimer]);

  useEffect(() => {
    if (!state) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setState(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [state]);

  // Reposition on scroll/resize while open
  useEffect(() => {
    if (!state) return;
    const reposition = () => {
      const pending = pendingRef.current;
      if (!pending) return;
      setState({
        slug: pending.slug,
        anchorRect: pending.el.getBoundingClientRect(),
      });
    };
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    return () => {
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  }, [state]);

  const value = useMemo<VocabHoverContextValue>(
    () => ({ open, scheduleClose, cancelClose }),
    [open, scheduleClose, cancelClose],
  );

  return (
    <VocabHoverContext.Provider value={value}>
      {children}
      {state ? (
        <VocabHoverCardPortal
          slug={state.slug}
          anchorRect={state.anchorRect}
          onMouseEnter={cancelClose}
          onMouseLeave={scheduleClose}
        />
      ) : null}
    </VocabHoverContext.Provider>
  );
}

function VocabHoverCardPortal({
  slug,
  anchorRect,
  onMouseEnter,
  onMouseLeave,
}: {
  slug: string;
  anchorRect: DOMRect;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}) {
  const { data, isLoading } = useVocabTermBySlug(slug);

  // Position the card under the anchor by default, flip above if there
  // isn't room below. Clamp to viewport horizontally.
  const viewportH = window.innerHeight;
  const viewportW = window.innerWidth;
  const spaceBelow = viewportH - anchorRect.bottom;
  const placeAbove = spaceBelow < 280 && anchorRect.top > 280;
  const top = placeAbove
    ? Math.max(8, anchorRect.top - CARD_MARGIN)
    : Math.min(viewportH - 8, anchorRect.bottom + CARD_MARGIN);
  let left = anchorRect.left + anchorRect.width / 2 - CARD_WIDTH / 2;
  left = Math.max(8, Math.min(viewportW - CARD_WIDTH - 8, left));

  const transform = placeAbove ? "translateY(-100%)" : undefined;

  return createPortal(
    <div
      role="dialog"
      aria-label={data ? `Vocabulary: ${data.term}` : "Vocabulary preview"}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      style={{
        position: "fixed",
        top,
        left,
        width: CARD_WIDTH,
        transform,
        zIndex: 9999,
      }}
      className="rounded-xl border border-slate-200 bg-white shadow-2xl ring-1 ring-black/5 overflow-hidden text-slate-900"
    >
      {isLoading && (
        <div className="p-4 text-sm text-slate-500">Loading definition…</div>
      )}
      {!isLoading && !data && (
        <div className="p-4 text-sm text-slate-500">
          <div className="font-semibold text-slate-700">
            “{slug.replace(/-/g, " ")}”
          </div>
          <div className="mt-1">No definition yet.</div>
        </div>
      )}
      {data ? (
        <Link
          to={`/vocab/${data.slug}`}
          className="block group focus:outline-none"
          aria-label={`Open vocab entry for ${data.term}`}
        >
          {data.image_url ? (
            <div className="aspect-video w-full overflow-hidden bg-slate-100">
              <img
                src={data.image_url}
                alt={data.image_alt ?? data.term}
                className="h-full w-full object-cover transition-transform group-hover:scale-[1.02]"
                loading="lazy"
              />
            </div>
          ) : null}
          <div className="p-4">
            <div className="flex items-baseline gap-2">
              <h3 className="text-lg font-bold leading-tight text-slate-900 group-hover:text-[#B87333]">
                {data.term}
              </h3>
              {data.part_of_speech ? (
                <span className="text-xs uppercase tracking-wide text-slate-500">
                  {data.part_of_speech}
                </span>
              ) : null}
            </div>
            {data.pronunciation ? (
              <div className="mt-0.5 text-xs text-slate-500">
                {data.pronunciation}
              </div>
            ) : null}
            <p className="mt-2 text-sm text-slate-700 line-clamp-4">
              {data.definition}
            </p>
            {data.category ? (
              <div className="mt-3 text-xs text-slate-500">
                in{" "}
                <span className="font-medium text-slate-700">
                  {data.category.name}
                </span>
              </div>
            ) : null}
            <div className="mt-3 text-xs font-semibold text-[#B87333] group-hover:underline">
              View full entry →
            </div>
          </div>
        </Link>
      ) : null}
    </div>,
    document.body,
  );
}
